/**
 * QUIPWA — the Fichero skin's sounds, from the app's side
 *
 * A device clicks; a flat button does not. With Fichero on and its sound
 * turned on (Sonido, under the skin in Ajustes → Apariencia e idioma and in
 * Apariencia → En este dispositivo; or from the palette), the skin's player
 * makes a short sound for each activation: a key, a latch, a rubber key, a
 * stamp… (recipes.ts). Plan: docs/plan/examen-pedidos-y-calidad-de-vida.md §3.
 *
 * The player (fichero-sounds.ts) is its own chunk, inside the skin's budget
 * (scripts/check-bundle.mjs): it is downloaded only while the skin and its
 * sound are on. It hears clicks by itself and knows each control by its role
 * (which-sound.ts), so screens only `cue` what is not a click: a keyboard
 * shortcut (rating, flipping, confidence, a radio row's arrows) and the end
 * of a session (the receipt). Until it is loaded, a cue does nothing.
 *
 * It keeps quiet with a microphone open or a voice reading (quiet.ts,
 * lib/voice/activity.ts), and it never holds an action back: the sound is
 * made after the press has been handled.
 *
 * ```ts
 * cue('thud')                 // the rating's rubber key, from a keyboard shortcut
 * const off = turnSoundsOn()  // ThemeInjector, while the skin and its sound are on
 * ```
 */
import { useThemeStore } from '../theme/theme-store'
import { useMicrophone, useSpeaking } from '../voice/activity'
import { soundBlocked, type SoundBlock } from './quiet'
import type { SoundName } from './recipes'

export type { SoundName } from './recipes'

let player: ((name: SoundName) => void) | null = null

/** A sound for something that was not a click. */
export function cue(name: SoundName): void {
  player?.(name)
}

/** fichero-sounds.ts registers itself here, and unregisters with null. */
export function setSoundPlayer(next: ((name: SoundName) => void) | null): void {
  player = next
}

/** Why no sound may play right now, or null if one may. */
export function soundBlockedNow(): SoundBlock | null {
  const theme = useThemeStore.getState()
  return soundBlocked({
    skin: theme.skin,
    on: theme.sound,
    volume: theme.soundVolume,
    microphones: useMicrophone.getState().open,
    speaking: useSpeaking.getState().id !== null,
    hidden: typeof document !== 'undefined' && document.visibilityState === 'hidden',
  })
}

/** Loads the player and starts it; the returned function stops it. */
export function turnSoundsOn(): () => void {
  let wanted = true
  let stop: (() => void) | null = null
  import('./fichero-sounds').then(
    (sounds) => {
      if (wanted) stop = sounds.installSounds()
    },
    (err: unknown) => console.warn('[Sound] Could not load the sounds', err),
  )
  return () => {
    wanted = false
    stop?.()
    stop = null
  }
}
